import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { AgendaRow } from '../../../services/dashboard.service';
import { Reservation } from '../../../models/sala.model';
import { UiStateComponent } from '../../ui/ui-state.component';
import { UiSkeletonComponent } from '../../ui/ui-skeleton.component';
import { formatDayLabel, formatHourMin, formatMoney, formatTimeRange, peopleLabel, paymentLabel, reservationStatusLabel, reservationTone, spaceImage } from './dashboard-view.util';

interface HourLabel { min: number; label: string; pct: number; }

/**
 * Agenda del día: espacios por fila, horas por columna y reservas como bloques
 * proporcionales a su horario real. Es una vista resumida de la operación
 * diaria; la gestión avanzada sigue en el módulo Calendario.
 */
@Component({
  selector: 'app-dashboard-agenda',
  standalone: true,
  imports: [CommonModule, UiStateComponent, UiSkeletonComponent],
  template: `
    <section class="agenda" aria-labelledby="agenda-title">
      <header class="head">
        <div>
          <div class="title-line">
            <h3 id="agenda-title">{{ title }}</h3>
            <span class="summary" [class.has-items]="reservationCount > 0">
              <i class="bi" [ngClass]="reservationCount ? 'bi-calendar2-check' : 'bi-calendar2'" aria-hidden="true"></i>
              {{ reservationSummary }}
            </span>
          </div>
          <p>Ocupación real por espacio y horario.</p>
        </div>
        <div class="controls">
          <div class="day-navigation">
            <button type="button" class="icon-btn" aria-label="Día anterior" (click)="prev.emit()"><i class="bi bi-chevron-left" aria-hidden="true"></i></button>
            <span class="date-chip"><i class="bi bi-calendar3" aria-hidden="true"></i>{{ fecha }}</span>
            <button type="button" class="icon-btn" aria-label="Día siguiente" (click)="next.emit()"><i class="bi bi-chevron-right" aria-hidden="true"></i></button>
          </div>
          <button type="button" class="ghost-btn today-btn" (click)="today.emit()">Hoy</button>
          <button type="button" class="ghost-btn accent" (click)="openCalendar.emit()"><i class="bi bi-calendar-week" aria-hidden="true"></i> Ver calendario</button>
        </div>
      </header>

      <app-ui-skeleton *ngIf="loading && !rows.length" variant="list" [count]="3"></app-ui-skeleton>

      <app-ui-state
        *ngIf="!loading && error"
        tone="error"
        icon="bi-wifi-off"
        title="No pudimos cargar la agenda"
        message="Revisa tu conexión e inténtalo de nuevo."
        actionLabel="Reintentar"
        (action)="retry.emit()"
      ></app-ui-state>

      <app-ui-state
        *ngIf="!loading && !error && !rows.length"
        tone="empty"
        icon="bi-door-open"
        title="Aún no tienes espacios operativos"
        message="Crea tu primer espacio para empezar a recibir reservas."
      ></app-ui-state>

      <!-- Escritorio/tablet: cuadrícula horas x espacios -->
      <div class="planner-shell" *ngIf="!loading && !error && rows.length && !compact">
      <div class="planner">
        <div class="spaces">
          <div class="ruler-spacer"><i class="bi bi-building" aria-hidden="true"></i> Espacios</div>
          <div class="space-cell" *ngFor="let row of rows; let i = index" [style.height.px]="rowHeight(row)">
            <img *ngIf="image(row.space, i) as imageUrl" [src]="imageUrl" [alt]="''" loading="lazy" (error)="onImageError(row.space.space_id || i)" />
            <span class="space-avatar" *ngIf="!image(row.space, i)" aria-hidden="true"><i class="bi bi-building"></i></span>
            <span class="space-meta">
              <strong>{{ row.space.title }}</strong>
              <small>{{ row.space.capacity ? peopleLabel(row.space.capacity) : 'Sin aforo' }} <b aria-hidden="true">·</b> <em [class.busy]="row.blocks.length">{{ row.blocks.length ? (row.blocks.length + (row.blocks.length === 1 ? ' reserva' : ' reservas')) : 'Libre' }}</em></small>
            </span>
          </div>
        </div>
        <div class="timeline">
          <div class="ruler">
            <span class="hour" *ngFor="let h of hourLabels; let first = first; let last = last" [class.first]="first" [class.last]="last" [style.left.%]="h.pct">{{ h.label }}</span>
          </div>
          <div class="lanes" [style.background-size]="gridSize">
            <div class="lane" *ngFor="let row of rows" [style.height.px]="rowHeight(row)">
              <button
                *ngFor="let block of row.blocks"
                type="button"
                class="block"
                [ngClass]="'tone-' + tone(block.reservation)"
                [style.left.%]="left(block.startMin)"
                [style.width.%]="width(block.startMin, block.endMin)"
                [style.top.%]="(block.lane / block.lanes) * 100"
                [style.height.%]="100 / block.lanes"
                [attr.title]="tooltip(block.reservation, row)"
                [attr.aria-label]="tooltip(block.reservation, row)"
                (click)="select(block.reservation)"
              >
                <span class="block-inner">
                  <strong>{{ block.reservation.customer_name || 'Reserva' }}</strong>
                  <small>{{ range(block.reservation) }} · {{ block.reservation.number_of_people || 0 }} pers.</small>
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
      <p class="availability-note" *ngIf="reservationCount === 0"><i class="bi bi-stars" aria-hidden="true"></i><span><strong>Sin reservas para esta fecha</strong> Todos los espacios están libres en el horario mostrado.</span></p>
      </div>

      <!-- Móvil: lista cronológica del día -->
      <section class="mobile-agenda" *ngIf="!loading && !error && rows.length && compact" aria-label="Horario y reservas del día">
        <div class="mobile-window">
          <span><i class="bi bi-clock" aria-hidden="true"></i> Horario visible</span>
          <strong>{{ formatHourMin(windowStart) }} – {{ formatHourMin(windowEnd) }}</strong>
        </div>
        <div class="mobile-ruler" aria-label="Escala horaria">
          <span *ngFor="let h of hourLabels">{{ h.label }}</span>
        </div>
      <ol class="mobile-list">
        <li *ngFor="let item of chronological">
          <button type="button" class="m-row" [ngClass]="'tone-' + tone(item.reservation)" (click)="select(item.reservation)">
            <span class="m-icon" aria-hidden="true"><i class="bi bi-calendar2-check"></i></span>
            <span class="m-time">{{ range(item.reservation) }}</span>
            <span class="m-body">
              <strong>{{ item.reservation.customer_name || 'Reserva' }}</strong>
              <small>{{ item.row.space.title }} · {{ item.reservation.number_of_people || 0 }} pers.</small>
              <em>{{ status(item.reservation) }}</em>
            </span>
          </button>
        </li>
        <li class="m-empty" *ngIf="!chronological.length"><i class="bi bi-calendar2-check" aria-hidden="true"></i><strong>Agenda despejada</strong><span>No hay reservas para este día.</span></li>
      </ol>
      </section>

      <!-- Detalle rápido (datos reales de la reserva) -->
      <aside class="detail" *ngIf="selected as reservation">
        <header>
          <div>
            <small>RESERVA</small>
            <strong>{{ reservation.customer_name || 'Reserva' }}</strong>
          </div>
          <button type="button" class="icon-btn" aria-label="Cerrar detalle" (click)="close()"><i class="bi bi-x-lg" aria-hidden="true"></i></button>
        </header>
        <dl>
          <div><dt><i class="bi bi-building" aria-hidden="true"></i> Espacio</dt><dd>{{ reservation.space_title }}</dd></div>
          <div><dt><i class="bi bi-calendar-event" aria-hidden="true"></i> Fecha</dt><dd>{{ day(reservation) }}</dd></div>
          <div><dt><i class="bi bi-clock" aria-hidden="true"></i> Horario</dt><dd>{{ range(reservation) }}</dd></div>
          <div><dt><i class="bi bi-people" aria-hidden="true"></i> Personas</dt><dd>{{ reservation.number_of_people }}</dd></div>
          <div><dt><i class="bi bi-person" aria-hidden="true"></i> Cliente</dt><dd>{{ contact(reservation) }}</dd></div>
          <div><dt><i class="bi bi-info-circle" aria-hidden="true"></i> Estado</dt><dd>{{ status(reservation) }} · {{ pay(reservation) }}</dd></div>
          <div *ngIf="reservation.total_price"><dt><i class="bi bi-cash" aria-hidden="true"></i> Total</dt><dd>{{ money(reservation.total_price) }}</dd></div>
        </dl>
        <div class="detail-actions">
          <button type="button" class="primary" (click)="openReservation.emit(reservation)">Abrir en Reservas</button>
          <button type="button" class="ghost" (click)="openSpace.emit(reservation.space_id)">Ver espacio</button>
        </div>
      </aside>
    </section>
  `,
  styles: [`
    :host { display: block; min-width: 0; height: 100%; }
    .agenda { position: relative; box-sizing: border-box; height: 100%; min-width: 0; padding: 1.05rem 1.15rem 1.15rem; border: 1px solid var(--ts-card-border, var(--ts-border)); border-radius: var(--ts-radius-lg); background: var(--ts-surface); box-shadow: var(--ts-card-shadow, var(--ts-shadow-sm)); }
    .head { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; flex-wrap: wrap; margin-bottom: .95rem; }
    .head h3 { margin: 0 0 .18rem; color: var(--ts-text); font-family: "Plus Jakarta Sans", Inter, sans-serif; font-size: 1.02rem; letter-spacing: -.02em; }
    .title-line { display: flex; align-items: center; gap: .55rem; flex-wrap: wrap; }
    .summary { display: inline-flex; align-items: center; gap: .28rem; min-height: 23px; padding: 0 .48rem; color: var(--ts-text-muted); border: 1px solid var(--ts-border-faint-3); border-radius: 999px; background: var(--ts-surface-soft); font-size: .61rem; font-weight: 750; }
    .summary.has-items { color: var(--ts-text-blue-300-4); border-color: var(--ts-border-blue-350-a35-4); background: var(--ts-bg-blue-400-a12); }
    .head p { margin: 0; color: var(--ts-text-muted); font-size: .72rem; }
    .controls { display: flex; align-items: center; justify-content: flex-end; gap: .4rem; flex-wrap: wrap; }
    .day-navigation { display:flex;align-items:center;gap:.35rem;min-width:0; }
    .date-chip { display: inline-flex; align-items: center; gap: .42rem; height: 34px; padding: 0 .7rem; color: var(--ts-text); border: 1px solid var(--ts-border); border-radius: 9px; background: var(--ts-bg-elevated); font-size: .72rem; font-weight: 650; }
    .icon-btn, .ghost-btn { display: inline-flex; align-items: center; gap: .4rem; height: 34px; padding: 0 .65rem; color: var(--ts-text-muted); border: 1px solid var(--ts-border); border-radius: 9px; background: transparent; font: inherit; font-size: .72rem; font-weight: 650; cursor: pointer; }
    .icon-btn { width: 34px; padding: 0; justify-content: center; }
    .icon-btn:hover, .ghost-btn:hover { color: var(--ts-text); border-color: var(--ts-border-blue-400-a40); background: var(--ts-surface-hover); }
    .ghost-btn.accent { color: var(--ts-text-blue-300-4); border-color: var(--ts-border-blue-350-a35-4); background: var(--ts-bg-blue-400-a12); }
    .planner-shell { min-width: 0; overflow: hidden; border: 1px solid var(--ts-border-faint-3); border-radius: 14px; background: var(--ts-surface-soft); }
    .planner { display: flex; align-items: flex-start; gap: 0; min-width: 0; background: var(--ts-surface); }
    .spaces { flex: 0 0 188px; border-right: 1px solid var(--ts-border); background: var(--ts-surface-soft); }
    .ruler-spacer { box-sizing: border-box; display: flex; align-items: center; gap: .38rem; height: 36px; padding: 0 .68rem; color: var(--ts-text-subtle); border-bottom: 1px solid var(--ts-border); font-size: .58rem; font-weight: 800; letter-spacing: .05em; text-transform: uppercase; }
    .space-cell { box-sizing: border-box; display: flex; align-items: center; gap: .58rem; padding: .42rem .68rem; min-height: 56px; border-bottom: 1px solid var(--ts-border-faint); }
    .space-cell img { width: 42px; height: 42px; flex: 0 0 42px; object-fit: cover; border: 1px solid var(--ts-border-faint-3); border-radius: 11px; background: var(--ts-surface-2); }
    .space-avatar { display: grid; place-items: center; width: 42px; height: 42px; flex: 0 0 42px; color: var(--ts-text-blue-300-4); border: 1px solid color-mix(in srgb, var(--ts-primary) 20%, var(--ts-border)); border-radius: 11px; background: color-mix(in srgb, var(--ts-primary) 8%, var(--ts-surface)); font-size: 1rem; }
    .space-meta { display: grid; min-width: 0; }
    .space-meta strong { overflow: hidden; color: var(--ts-text); font-size: .74rem; text-overflow: ellipsis; white-space: nowrap; }
    .space-meta small { overflow:hidden;color:var(--ts-text-subtle);font-size:.61rem;text-overflow:ellipsis;white-space:nowrap; }.space-meta small b{font-weight:400}.space-meta small em{color:var(--ts-success-text);font-style:normal;font-weight:750}.space-meta small em.busy{color:var(--ts-text-blue-300-4)}
    .timeline { flex: 1 1 auto; min-width: 0; overflow-x: auto; scrollbar-color: var(--ts-border-strong) transparent; scrollbar-width: thin; }
    .ruler { position: relative; height: 36px; min-width: 560px; border-bottom: 1px solid var(--ts-border); background: var(--ts-surface-soft); }
    .hour { position: absolute; top: 10px; color: var(--ts-text-subtle); font-size: .62rem; font-weight: 650; transform: translateX(-50%); white-space:nowrap; }.hour.first{transform:none}.hour.last{transform:translateX(-100%)}
    .lanes { position: relative; min-width: 560px; background-image: linear-gradient(to right, var(--ts-border-faint-3) 0 1px, transparent 1px); background-repeat: repeat-x; }
    .lane { position: relative; border-bottom: 1px solid var(--ts-border-faint); min-height: 56px; }
    .lane:last-child { border-bottom: 0; }
    .availability-note { display: flex; align-items: center; gap: .58rem; margin: 0; padding: .68rem .78rem; color: var(--ts-text-muted); border-top: 1px solid var(--ts-border-faint-3); background: color-mix(in srgb, var(--ts-success) 6%, var(--ts-surface)); font-size: .66rem; }
    .availability-note > i { display: grid; place-items: center; width: 28px; height: 28px; flex: 0 0 28px; color: var(--ts-success-text); border-radius: 9px; background: color-mix(in srgb, var(--ts-success) 12%, transparent); }
    .availability-note strong { color: var(--ts-text); }
    .block { position: absolute; padding: 0; border: 0; background: transparent; cursor: pointer; }
    .block-inner { display: grid; gap: .05rem; overflow: hidden; height: calc(100% - 4px); margin: 2px 3px; padding: .35rem .5rem; text-align: left; border-radius: 8px; border-left: 3px solid transparent; }
    .block-inner strong { overflow: hidden; font-size: .68rem; font-weight: 750; text-overflow: ellipsis; white-space: nowrap; }
    .block-inner small { overflow: hidden; font-size: .6rem; text-overflow: ellipsis; white-space: nowrap; }
    .block:hover .block-inner, .block:focus-visible .block-inner { filter: brightness(1.04); box-shadow: 0 4px 14px var(--ts-shadow-gray-1000-a18); }
    .block:focus-visible { outline: 2px solid var(--ts-border-blue-400-a28-2); outline-offset: 1px; border-radius: 9px; }
    .tone-blue .block-inner, .m-row.tone-blue { color: var(--ts-text-blue-250-7); border-left-color: var(--ts-bg-blue-400-9); background: var(--ts-bg-blue-400-a19); }
    .tone-violet .block-inner, .m-row.tone-violet { color: var(--ts-text-indigo-200); border-left-color: var(--ts-indigo); background: var(--ts-bg-indigo-350-a15); }
    .tone-green .block-inner, .m-row.tone-green { color: var(--ts-text-green-350-2); border-left-color: var(--ts-success); background: var(--ts-bg-green-500-a22); }
    .tone-orange .block-inner, .m-row.tone-orange { color: var(--ts-text-amber-350); border-left-color: var(--ts-warning); background: var(--ts-bg-orange-450-a22); }
    .mobile-list { display: grid; gap: .5rem; margin: 0; padding: 0; list-style: none; }
    .mobile-agenda { display:grid;gap:.65rem;min-width:0; }
    .mobile-window { display:flex;align-items:center;justify-content:space-between;gap:.7rem;padding:.62rem .7rem;color:var(--ts-text-muted);border:1px solid var(--ts-border);border-radius:11px;background:var(--ts-surface-soft);font-size:.65rem; }
    .mobile-window span{display:inline-flex;align-items:center;gap:.4rem}.mobile-window i{color:var(--ts-text-blue-300-4)}.mobile-window strong{color:var(--ts-text);font-size:.7rem;white-space:nowrap}
    .mobile-ruler { display:flex;align-items:center;justify-content:space-between;gap:.25rem;min-width:0;padding:.45rem .55rem;color:var(--ts-text-subtle);border:1px solid var(--ts-border-faint-3);border-radius:10px;background:linear-gradient(to right,var(--ts-border-faint-3) 0 1px,transparent 1px);font-size:.56rem;font-weight:700; }
    .m-row { display: grid; grid-template-columns:34px 78px minmax(0,1fr); align-items:center; gap: .6rem; width: 100%; padding: .65rem .7rem; text-align: left; font: inherit; border: 1px solid var(--ts-border); border-left-width: 3px; border-radius: 11px; cursor: pointer; }
    .m-icon { display:grid;place-items:center;width:34px;height:34px;border-radius:9px;background:color-mix(in srgb,currentColor 10%,var(--ts-surface));font-size:.8rem; }
    .m-time { font-size: .66rem; font-weight: 800; }
    .m-body { display: grid; min-width: 0; }
    .m-body strong { overflow: hidden; font-size: .74rem; text-overflow: ellipsis; white-space: nowrap; }
    .m-body small { color: var(--ts-text-muted); font-size: .64rem; }
    .m-body em { width:max-content;margin-top:.28rem;padding:.18rem .4rem;color:inherit;border:1px solid currentColor;border-radius:999px;font-size:.55rem;font-style:normal;font-weight:750;opacity:.86; }
    .m-empty { display:grid;justify-items:center;gap:.25rem;padding:1.2rem .8rem;color:var(--ts-text-muted);border:1px dashed var(--ts-border);border-radius:12px;background:var(--ts-surface-soft);font-size:.7rem;text-align:center; }
    .m-empty i { display:grid;place-items:center;width:38px;height:38px;margin-bottom:.15rem;color:var(--ts-success-text);border-radius:11px;background:color-mix(in srgb,var(--ts-success) 10%,var(--ts-surface));font-size:1rem; }.m-empty strong{color:var(--ts-text);font-size:.76rem}
    .detail { position: absolute; z-index: 5; top: 3.3rem; right: .8rem; width: min(300px, calc(100% - 1.6rem)); padding: .85rem .9rem; border: 1px solid var(--ts-border-strong); border-radius: var(--ts-radius-md); background: var(--ts-bg-elevated); box-shadow: var(--ts-shadow-lg); animation: detail-in .18s ease-out both; }
    @keyframes detail-in { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: none; } }
    .detail header { display: flex; align-items: flex-start; justify-content: space-between; gap: .6rem; margin-bottom: .6rem; }
    .detail header small { display: block; color: var(--ts-text-subtle); font-size: .58rem; font-weight: 800; letter-spacing: .08em; }
    .detail header strong { color: var(--ts-text); font-size: .82rem; }
    .detail dl { display: grid; gap: .35rem; margin: 0 0 .75rem; }
    .detail dl > div { display: flex; align-items: baseline; justify-content: space-between; gap: .6rem; }
    .detail dt { display: inline-flex; align-items: center; gap: .35rem; color: var(--ts-text-muted); font-size: .66rem; }
    .detail dd { margin: 0; color: var(--ts-text); font-size: .7rem; font-weight: 650; text-align: right; }
    .detail-actions { display: flex; gap: .45rem; }
    .detail-actions button { flex: 1 1 0; min-height: 34px; border-radius: 9px; font: inherit; font-size: .7rem; font-weight: 750; cursor: pointer; }
    .detail-actions .primary { color: var(--ts-on-primary); border: 0; background: linear-gradient(135deg, var(--ts-primary), var(--ts-indigo)); }
    .detail-actions .ghost { color: var(--ts-text); border: 1px solid var(--ts-border); background: transparent; }
    @media (max-width: 900px) { .planner-shell { display: none; } }
    @media (min-width: 901px) { .mobile-agenda { display: none; } }
    @media (max-width: 680px) {
      .agenda { padding: .9rem; }
      .head { display: grid; gap: .75rem; }
      .controls { display:grid;grid-template-columns:minmax(0,1fr) auto;gap:.5rem;width:100%; }
      .day-navigation { grid-column:1/-1;display:grid;grid-template-columns:38px minmax(0,1fr) 38px;gap:.45rem; }
      .day-navigation .icon-btn{width:38px;height:40px}.date-chip{justify-content:center;height:40px;min-width:0;padding:0 .45rem;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
      .today-btn,.ghost-btn.accent{height:40px;justify-content:center}.ghost-btn.accent{width:100%}
      .m-row { grid-template-columns:32px 68px minmax(0,1fr); gap:.48rem; }
    }
    @media (max-width: 430px) { .agenda{padding:.78rem}.controls{grid-template-columns:72px minmax(0,1fr)}.m-row{grid-template-columns:32px minmax(0,1fr)}.m-time{grid-column:2}.m-body{grid-column:2}.m-icon{grid-row:1/3}.mobile-ruler span:nth-child(even){display:none} }
  `],
})
export class DashboardAgendaComponent implements OnChanges {
  @Input() rows: AgendaRow[] = [];
  @Input() windowStart = 8 * 60;
  @Input() windowEnd = 22 * 60;
  @Input() selectedDate = '';
  @Input() loading = false;
  @Input() error = false;
  @Input() compact = false;

  @Output() prev = new EventEmitter<void>();
  @Output() next = new EventEmitter<void>();
  @Output() today = new EventEmitter<void>();
  @Output() openCalendar = new EventEmitter<void>();
  @Output() openReservation = new EventEmitter<Reservation>();
  @Output() openSpace = new EventEmitter<number>();
  @Output() retry = new EventEmitter<void>();

  selected: Reservation | null = null;
  private failedImages = new Set<number>();

  /**
   * Listas cacheadas: un getter que devolviera un arreglo nuevo en cada pasada
   * de detección haría que *ngFor entrara en un bucle infinito de render.
   */
  private hourLabelCache: HourLabel[] = [];
  private chronologicalCache: { reservation: Reservation; row: AgendaRow }[] = [];

  ngOnChanges(): void {
    this.failedImages.clear();
    this.hourLabelCache = this.buildHourLabels();
    this.chronologicalCache = this.buildChronological();
  }

  get fecha(): string { return formatDayLabel(this.selectedDate); }
  get title(): string { return this.selectedDate === this.localToday() ? 'Agenda de hoy' : 'Agenda del día'; }
  get reservationCount(): number { return this.chronologicalCache.length; }
  get reservationSummary(): string {
    return this.reservationCount === 1 ? '1 reserva' : `${this.reservationCount} reservas`;
  }
  get span(): number { return Math.max(60, this.windowEnd - this.windowStart); }
  get hours(): number { return Math.max(1, Math.round(this.span / 60)); }
  get gridSize(): string { return `${100 / this.hours}% 100%`; }

  get hourLabels(): HourLabel[] {
    return this.hourLabelCache;
  }

  private buildHourLabels(): HourLabel[] {
    const labels: HourLabel[] = [];
    for (let min = this.windowStart; min <= this.windowEnd; min += 120) {
      labels.push({ min, label: formatHourMin(min), pct: ((min - this.windowStart) / this.span) * 100 });
    }
    return labels;
  }

  get chronological(): { reservation: Reservation; row: AgendaRow }[] {
    return this.chronologicalCache;
  }

  private buildChronological(): { reservation: Reservation; row: AgendaRow }[] {
    const items: { reservation: Reservation; row: AgendaRow }[] = [];
    for (const row of this.rows) {
      for (const block of row.blocks) items.push({ reservation: block.reservation, row });
    }
    return items.sort((a, b) => (a.reservation.start_time || '').localeCompare(b.reservation.start_time || ''));
  }

  rowHeight(row: AgendaRow): number {
    const lanes = row.blocks.reduce((max, block) => Math.max(max, block.lanes), 1);
    return Math.max(56, lanes * 46);
  }

  left(startMin: number): number { return ((startMin - this.windowStart) / this.span) * 100; }
  width(startMin: number, endMin: number): number { return Math.max(1.6, ((endMin - startMin) / this.span) * 100); }

  tone(reservation: Reservation) { return reservationTone(reservation); }
  status(reservation: Reservation): string { return reservationStatusLabel(reservation); }
  pay(reservation: Reservation): string { return paymentLabel(reservation); }
  range(reservation: Reservation): string { return formatTimeRange(reservation.start_time, reservation.end_time); }
  day(reservation: Reservation): string { return formatDayLabel((reservation.reservation_date || reservation.date || '').slice(0, 10)); }
  money(value: string): string { return formatMoney(value); }
  image(space: AgendaRow['space'], index: number): string | null {
    return this.failedImages.has(space.space_id || index) ? null : spaceImage(space);
  }
  onImageError(key: number): void {
    this.failedImages.add(key);
  }
  peopleLabel = peopleLabel;
  formatHourMin = formatHourMin;

  contact(reservation: Reservation): string {
    const name = reservation.customer_name || reservation.guest_name || 'Sin cliente';
    const extra = reservation.customer?.email || reservation.guest_email || '';
    return extra ? `${name} · ${extra}` : name;
  }

  tooltip(reservation: Reservation, row: AgendaRow): string {
    return `${reservation.customer_name || 'Reserva'} · ${row.space.title} · ${this.range(reservation)} · ${this.status(reservation)} · ${this.pay(reservation)}`;
  }

  select(reservation: Reservation): void {
    this.selected = this.selected?.reservation_id === reservation.reservation_id ? null : reservation;
  }

  close(): void { this.selected = null; }

  private localToday(): string {
    const date = new Date();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }
}
